import type { CardData } from '../../types';
import { VSM_LIBRARY } from '../VSMLibrary';
import { useProjectStore } from '../../store/useProjectStore';

// Tamanhos calculados a partir do viewBox exato de cada SVG de produção (/public/assets/vsm/)
// Fórmula: width = round(90 * vbW/vbH). Para ícones muito largos: cap w=220.
// isEstoque (pulmao/supermercado/estoque) usa cardHeight como altura do shape.
const VSM_PNG_SIZES: Record<string, { width: number; height: number }> = {
  externo:      { width: 127, height: 90 },  // vb 57.0×40.4
  fornecedor:   { width: 127, height: 90 },
  cliente:      { width: 127, height: 90 },
  entrega:      { width: 150, height: 90 },  // vb 58.5×35.2
  estoque:      { width: 104, height: 80 },  // vb 24.8×19.1 (isEstoque)
  pulmao:       { width:  65, height: 110 }, // reduzido mais ~10% (isEstoque, portrait)
  supermercado: { width:  66, height: 110 }, // reduzido mais ~10% (isEstoque, portrait)
  // PCPM/MRP deve ser card de gestão limpo (não seta), alinhado ao topo das entidades.
  pcpm:         { width: 127, height: 90 },
  nivelamento:  { width: 127, height: 77 },  // altura reduzida 15% (heijunka compacto)
  kaizen:       { width: 141, height: 90 },  // vb 58.7×37.3
  fifo:         { width: 220, height: 65 },  // vb 47.9×14.2
  empurrado:    { width: 220, height: 55 },  // vb 49.6×12.3
  produto:      { width: 220, height: 62 },  // vb 49.8×14.0
  puxado:       { width: 104, height: 80 },  // alinhado visualmente ao estoque
  vaver:        { width: 104, height: 80 },  // alinhado visualmente ao estoque
  kanban_prod:  { width:  37, height: 28 },
  kanban_ret:   { width:  37, height: 28 },
  kanban_lote:  { width:  60, height: 46 },
  kanban_posto: { width:  46, height: 90 },  // vb 10.9×21.1 (portrait)
  kanban_sinal: { width: 117, height: 90 },  // vb 24.8×19.1
  comunicacao:  { width: 118, height: 40 },  // etiqueta compacta de fluxo de informação
  databox:      { width: 106, height: 90 },  // vb 57.3×48.6
  operador:     { width: 113, height: 90 },  // vb 14.3×11.4
  info_eletro:  { width: 127, height: 90 },  // PCPM/MRP card — mesmo tamanho do nivelamento
  info_manual:  { width: 220, height: 28 },  // vb 48.4×6.1
  caminhao:     { width: 110, height: 60 },
  transporte:   { width: 110, height: 60 },
  vertical_flow: { width: 44, height: 162 },
};

export function getCardDimensions(
  data: CardData,
  isDecision: boolean,
  isTimeScaleActive: boolean
): { width: number; height: number; decisionSize: number } {
  const vsmIconKey = data.vsmIcon as keyof typeof VSM_LIBRARY;
  const vsmConfig = vsmIconKey ? VSM_LIBRARY[vsmIconKey] : null;

  const VSM_ENTITY_PROCESS_WIDTH = 127;
  const VSM_PROCESS_DATABOX_WIDTH = 121;
  const VSM_PROCESS_HEIGHT = 90;
  const VSM_PROCESS_DATABOX_BODY_RATIO = 104 / 207;

  // Processo com DataBox usa largura visual menor para compensar o desenho interno do SVG.
  if (!isDecision && data.vsmIcon === 'processo') {
    const hasDataBox = !!((data as any).vsmCT || (data as any).vsmCO || (data as any).vsmDisp);
    const cardWidth = hasDataBox ? VSM_PROCESS_DATABOX_WIDTH : VSM_ENTITY_PROCESS_WIDTH;
    const cardHeight = hasDataBox ? Math.round(VSM_PROCESS_HEIGHT / VSM_PROCESS_DATABOX_BODY_RATIO) : VSM_PROCESS_HEIGHT;
    return { width: cardWidth, height: cardHeight, decisionSize: 160 };
  }

  if (!isDecision && ['externo', 'fornecedor', 'cliente'].includes(data.vsmIcon ?? '')) {
    const demandMix = ((data as any).vsmDemandMix ?? []) as Array<{ qty?: number }>;
    const activeMixRows = demandMix.filter(item => Number(item?.qty) > 0).length;
    const hasProduct = !!((data as any).vsmProduto || activeMixRows > 0);
    const hasFrequency = !!(data as any).vsmFrequencia;
    const hasDemand = !!((data as any).vsmDemanda || (data as any).entrada);
    const hasDeliveryLot = Number((data as any).vsmDeliveryLot) > 0;
    const legacyRows = activeMixRows === 0
      ? [(data as any).vsmLinha4, (data as any).vsmLinha5].filter(Boolean).length
      : 0;
    const dataBoxHeight =
      (hasProduct ? 20 : 0) +
      (hasFrequency ? 20 : 0) +
      activeMixRows * 18 +
      (hasDemand ? 20 : 0) +
      (hasDeliveryLot ? 20 : 0) +
      legacyRows * 20;
    const baseSize = VSM_PNG_SIZES[data.vsmIcon] ?? VSM_PNG_SIZES.externo;
    const normalizedDataBoxHeight = dataBoxHeight > 0 ? Math.max(60, dataBoxHeight) : 0;
    return { width: baseSize.width, height: baseSize.height + normalizedDataBoxHeight, decisionSize: 160 };
  }

  if (!isDecision && data.vsmIcon === 'produto' && (data as any).vsmOrientation === 'vertical') {
    const baseSize = VSM_PNG_SIZES.produto;
    return { width: baseSize.height, height: baseSize.width, decisionSize: 160 };
  }

  if (!isDecision && data.vsmIcon === 'vertical_flow') {
    const baseSize = VSM_PNG_SIZES.vertical_flow;
    return { width: baseSize.width, height: Math.max(120, Number((data as any).vsmFlowHeight) || baseSize.height), decisionSize: 160 };
  }

  if (!isDecision && ['caminhao', 'transporte'].includes(data.vsmIcon ?? '') && (data as any).transportDockParentId) {
    return { width: 176, height: 72, decisionSize: 160 };
  }

  const pngSize = data.vsmIcon ? VSM_PNG_SIZES[data.vsmIcon] : null;
  
  // Se Time Scale está ativo e há largura calculada, usar ela
  const cardWidth = isTimeScaleActive && data.calculatedWidth 
    ? data.calculatedWidth 
    : (vsmConfig?.defaultSize?.width || pngSize?.width || (isDecision ? 160 : 240));
  let cardHeight = vsmConfig?.defaultSize?.height || pngSize?.height || (isDecision ? 160 : 180);

  // Nivelamento (OXOX) em blueprint/technical: card 40% mais compacto na altura (request do produto).
  if (data.vsmIcon === 'nivelamento') {
    try {
      const designStyle = useProjectStore.getState().vsmDesignStyle;
      if (designStyle === 'blueprint' || designStyle === 'technical') {
        cardHeight = Math.round(cardHeight * 0.6);
      }
    } catch {
      // store indisponível em SSR/test — mantém altura base
    }
  }
  
  // Tamanho do losango para decisões
  const decisionSize = data.layoutType === 'classic' ? 200 : 160;
  
  return { width: cardWidth, height: cardHeight, decisionSize };
}

export function getCardClassNames(params: {
  isDecision: boolean;
  selected: boolean;
  isDragging: boolean;
  isHovered: boolean;
  isConnecting: boolean;
  isCreatingPostIt: boolean;
  dragEnabled: boolean;
  cardType?: string;
}): string {
  const { isDecision, selected, isDragging, isHovered, isConnecting, isCreatingPostIt, dragEnabled, cardType } = params;
  
  // Estado Base
  const baseClasses = 'absolute group select-none transition-all duration-200 ease-in-out';
  const isInitialOrFinal = cardType === 'card_inicial' || cardType === 'card_final';
  const baseShadow = isDecision || isInitialOrFinal ? '' : (selected ? '' : 'shadow-sm hover:shadow-sm');
  
  // Estado Hover
  const hoverClasses = isHovered && !isDragging && !isCreatingPostIt
    ? 'cursor-move'
    : isConnecting 
    ? 'cursor-crosshair' 
    : isCreatingPostIt
    ? ''
    : dragEnabled ? 'cursor-move' : 'cursor-default';
  
  // Estado Selected
  const selectedClasses = selected ? 'z-50' : 'z-30';
  
  // Estado Dragging - Sem sombra para início/fim
  const draggingClasses = isDragging 
    ? (isInitialOrFinal ? 'scale-[1.02] opacity-90' : 'shadow-sm scale-[1.02] opacity-90')
    : '';
  
  return `${baseClasses} ${baseShadow} ${hoverClasses} ${selectedClasses} ${draggingClasses}`;
}

export function getActionStatusColor(status: string): string {
  switch (status) {
    case 'done': return '#10B981'; // Verde
    case 'doing': return '#3B82F6'; // Azul
    case 'todo': return '#6B7280'; // Cinza
    default: return '#6B7280';
  }
}

export function isVsmCellProcessCard(data: CardData): boolean {
  // Cards processo que pertencem a uma célula (vsmFlowCellId) DEVEM continuar renderizando
  // como processo-databox.svg. A "célula" é visualizada pelo wrapper pontilhado desenhado
  // pelo VsmCanvas usando vsmFlowCellX/Y/W/H — não por um SVG por membro.
  // Mantemos a função como hook explícito para futuras consolidações (ex: card único
  // representando a célula inteira), mas no estado atual nenhum card cumpre esse papel.
  void data;
  return false;
}
